import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  DollarSign, 
  TrendingUp, 
  AlertTriangle, 
  Users, 
  Package, 
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Calendar,
  Filter,
  Download,
  Clock,
  Check
} from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobRecap() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isOfficeOrOwner = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [laborBreakdown, setLaborBreakdown] = useState(null);
  const [materialBreakdown, setMaterialBreakdown] = useState(null);
  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [materialType, setMaterialType] = useState('all');
  const [sortField, setSortField] = useState('jobNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  // Extract unique customers from summary jobs
  const customers = useMemo(() => {
    if (!summary?.jobs) return [];
    const customerMap = new Map();
    summary.jobs.forEach(job => {
      if (job.customerName && job.customerName !== 'Unknown') {
        customerMap.set(job.customerName, job.customerName);
      }
    });
    return Array.from(customerMap.values()).sort();
  }, [summary]);

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    if (!summary?.jobs) return [];
    let jobs = selectedCustomer === 'all' 
      ? summary.jobs 
      : summary.jobs.filter(job => job.customerName === selectedCustomer);
    
    // Sort jobs
    jobs = [...jobs].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle numeric fields
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle string/null values
      aVal = aVal || '';
      bVal = bVal || '';
      
      if (sortDirection === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });
    
    return jobs;
  }, [summary, selectedCustomer, sortField, sortDirection]);

  // Helper to handle column sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Calculate days since last labor
  const getDaysSinceLastLabor = (lastLaborDate) => {
    if (!lastLaborDate) return null;
    const last = new Date(lastLaborDate);
    const now = new Date();
    const diffTime = Math.abs(now - last);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchBreakdowns();
  }, [selectedJob, dateFrom, dateTo, materialType]);

  const fetchData = async () => {
    try {
      const summaryRes = await axios.get(`${API}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(summaryRes.data);
      await fetchBreakdowns();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBreakdowns = async () => {
    try {
      const laborParams = new URLSearchParams();
      if (selectedJob && selectedJob !== 'all') laborParams.append('jobId', selectedJob);
      if (dateFrom) laborParams.append('dateFrom', dateFrom);
      if (dateTo) laborParams.append('dateTo', dateTo);

      const materialParams = new URLSearchParams();
      if (selectedJob && selectedJob !== 'all') materialParams.append('jobId', selectedJob);
      if (materialType && materialType !== 'all') materialParams.append('type', materialType);

      const [laborRes, materialRes] = await Promise.all([
        axios.get(`${API}/dashboard/labor-breakdown?${laborParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dashboard/material-breakdown?${materialParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setLaborBreakdown(laborRes.data);
      setMaterialBreakdown(materialRes.data);
    } catch (error) {
      console.error('Failed to fetch breakdowns:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Export Labor table to Excel
  const exportLaborToExcel = () => {
    if (!laborBreakdown?.entries || laborBreakdown.entries.length === 0) return;
    
    const data = laborBreakdown.entries.map(entry => ({
      'Date': entry.date,
      'Job #': entry.jobNumber,
      'PO #': entry.poNumber,
      'Name': entry.fullName,
      'Craft': entry.craftName || '',
      'Classification': entry.classificationName,
      'Shift': entry.shift || 'Day',
      'ST Hours': entry.hoursST,
      'OT Hours': entry.hoursOT,
      'DT Hours': entry.hoursDT,
      'Total Hours': entry.totalHours,
      'Charges ($)': entry.totalCharge
    }));
    
    // Add summary row
    data.push({
      'Date': 'TOTAL',
      'Job #': '',
      'PO #': '',
      'Name': '',
      'Craft': '',
      'Classification': '',
      'Shift': '',
      'ST Hours': laborBreakdown.totalHoursST || 0,
      'OT Hours': laborBreakdown.totalHoursOT || 0,
      'DT Hours': laborBreakdown.totalHoursDT || 0,
      'Total Hours': laborBreakdown.totalHours || 0,
      'Charges ($)': laborBreakdown.totalLaborCharges || 0
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Labor Charges');
    XLSX.writeFile(wb, `labor_charges_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export Material table to Excel
  const exportMaterialToExcel = () => {
    if (!materialBreakdown?.entries || materialBreakdown.entries.length === 0) return;
    
    const data = materialBreakdown.entries.map(entry => ({
      'Job #': entry.jobNumber,
      'Customer PO': entry.customerPoNumber,
      'Material PO': entry.materialPoNumber,
      'Vendor': entry.vendorName,
      'Type': entry.type,
      'Amount ($)': entry.amount,
      'Markup ($)': entry.markupAmount,
      'Markup %': entry.markupPct,
      'Taxes ($)': entry.taxes,
      'Total ($)': entry.totalCost
    }));
    
    // Add summary row
    data.push({
      'Job #': 'TOTAL',
      'Customer PO': '',
      'Material PO': '',
      'Vendor': '',
      'Type': '',
      'Amount ($)': materialBreakdown.totalAmount || 0,
      'Markup ($)': materialBreakdown.totalMarkup || 0,
      'Markup %': '',
      'Taxes ($)': '',
      'Total ($)': materialBreakdown.totalMaterialCharges || 0
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Material Charges');
    XLSX.writeFile(wb, `material_charges_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className='p-4 md:p-8'>
        <div className='text-center text-slate-600'>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className='p-4 md:p-8 space-y-6' data-testid='job-recap-page'>
      {/* Header */}
      <div>
        <h1 className='text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900'>Job Recap Dashboard</h1>
        <p className='mt-2 text-lg text-slate-700'>Overview of all job costs, labor, and materials</p>
      </div>

      {/* High-Level Summary Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <DollarSign className='w-5 h-5 text-[#1A3668]' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total PO Value</p>
            </div>
            <p className='text-3xl font-bold text-[#1A3668]'>{formatCurrency(summary?.totalPoValue)}</p>
            <p className='text-sm text-slate-500 mt-1'>{summary?.jobCount || 0} active jobs</p>
          </CardContent>
        </Card>

        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <TrendingUp className='w-5 h-5 text-amber-600' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Charges</p>
            </div>
            <p className='text-3xl font-bold text-slate-800'>
              {formatCurrency((summary?.totalCharges || 0) + (summary?.totalPendingAmount || 0))}
            </p>
            <div className='flex flex-wrap gap-3 mt-1 text-xs text-slate-500'>
              <span>Labor: {formatCurrency(summary?.totalLaborCharges)}</span>
              <span>Material: {formatCurrency(summary?.totalMaterialCharges)}</span>
            </div>
            {summary?.totalPendingAmount > 0 && (
              <div className='mt-2 pt-2 border-t border-slate-200 text-xs'>
                <span className='text-amber-600 font-medium'>
                  Pending Charges: {formatCurrency(summary?.totalPendingAmount)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <DollarSign className='w-5 h-5 text-[#16A34A]' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>PO Balance</p>
            </div>
            <p className={`text-3xl font-bold ${summary?.totalPoBalanceWithPending < 0 ? 'text-red-600' : 'text-[#16A34A]'}`}>
              {formatCurrency(summary?.totalPoBalanceWithPending)}
            </p>
            {summary?.totalPendingAmount > 0 && (
              <div className='mt-1 text-xs text-slate-500'>
                <span>Pending Charges: <span className='text-amber-600 font-medium'>{formatCurrency(summary?.totalPendingAmount)}</span></span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='border-slate-200 shadow-sm rounded-sm'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-2'>
              <AlertTriangle className='w-5 h-5 text-red-500' />
              <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Alerts</p>
            </div>
            <p className='text-3xl font-bold text-red-500'>
              {(summary?.jobsNearPo || 0) + (summary?.jobsOverPo || 0)}
            </p>
            <div className='flex gap-2 mt-1 text-xs'>
              {summary?.jobsNearPo > 0 && (
                <Badge className='bg-amber-100 text-amber-800 rounded-sm'>{summary.jobsNearPo} near PO</Badge>
              )}
              {summary?.jobsOverPo > 0 && (
                <Badge className='bg-red-100 text-red-800 rounded-sm'>{summary.jobsOverPo} over PO</Badge>
              )}
              {(summary?.jobsNearPo === 0 && summary?.jobsOverPo === 0) && (
                <span className='text-slate-500'>All jobs within budget</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoiced Summary - Office/Owner only */}
      {isOfficeOrOwner && summary?.jobs && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardContent className='p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <Check className='w-5 h-5 text-[#1A3668]' />
                <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Total Invoiced</p>
              </div>
              <p className='text-3xl font-bold text-[#1A3668]'>
                {formatCurrency(summary.jobs.reduce((sum, j) => sum + (j.invoicedAmount || 0), 0))}
              </p>
              <p className='text-sm text-slate-500 mt-1'>
                across {summary.jobs.filter(j => (j.invoicedAmount || 0) > 0).length} jobs
              </p>
            </CardContent>
          </Card>
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardContent className='p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <Clock className='w-5 h-5 text-amber-600' />
                <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Uninvoiced</p>
              </div>
              <p className='text-3xl font-bold text-amber-600'>
                {formatCurrency(
                  ((summary?.totalCharges || 0) + (summary?.totalPendingAmount || 0)) -
                  summary.jobs.reduce((sum, j) => sum + (j.invoicedAmount || 0), 0)
                )}
              </p>
              <p className='text-sm text-slate-500 mt-1'>remaining to invoice</p>
            </CardContent>
          </Card>
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardContent className='p-4'>
              <div className='flex items-center gap-2 mb-2'>
                <TrendingUp className='w-5 h-5 text-[#16A34A]' />
                <p className='text-sm font-bold uppercase tracking-wider text-slate-500'>Invoiced %</p>
              </div>
              <p className='text-3xl font-bold text-[#16A34A]'>
                {((summary?.totalCharges || 0) + (summary?.totalPendingAmount || 0)) > 0
                  ? Math.round((summary.jobs.reduce((sum, j) => sum + (j.invoicedAmount || 0), 0) / ((summary?.totalCharges || 0) + (summary?.totalPendingAmount || 0))) * 100)
                  : 0}%
              </p>
              <p className='text-sm text-slate-500 mt-1'>of total charges</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Jobs Portfolio Table */}
      <Card className='border-slate-200 shadow-sm rounded-sm'>
        <CardHeader>
          <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
            <CardTitle className='text-2xl font-bold text-slate-800'>Jobs Portfolio</CardTitle>
            {/* Customer Filter */}
            {customers.length > 0 && (
              <div className='flex items-center gap-2'>
                <Filter className='w-4 h-4 text-slate-500' />
                <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                  <SelectTrigger className='w-[220px] rounded-sm border-2' data-testid="portfolio-customer-filter">
                    <SelectValue placeholder="Filter by Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer} value={customer}>
                        {customer}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className='overflow-x-auto'>
            <table className='w-full border-collapse'>
              <thead>
                <tr className='bg-slate-100 border-b-2 border-slate-200'>
                  <th 
                    className='text-left p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                    onClick={() => handleSort('jobNumber')}
                  >
                    <div className='flex items-center gap-1'>
                      Job #
                      {sortField === 'jobNumber' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                    </div>
                  </th>
                  <th 
                    className='text-left p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                    onClick={() => handleSort('description')}
                  >
                    <div className='flex items-center gap-1'>
                      Description
                      {sortField === 'description' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                    </div>
                  </th>
                  <th 
                    className='text-right p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                    onClick={() => handleSort('totalChargesWithPending')}
                  >
                    <div className='flex items-center justify-end gap-1'>
                      Total
                      {sortField === 'totalChargesWithPending' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                    </div>
                  </th>
                  {isOfficeOrOwner && (
                    <th 
                      className='text-right p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                      onClick={() => handleSort('invoicedAmount')}
                    >
                      <div className='flex items-center justify-end gap-1'>
                        <Check className='w-3 h-3' /> Invoiced
                        {sortField === 'invoicedAmount' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                      </div>
                    </th>
                  )}
                  <th 
                    className='text-right p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                    onClick={() => handleSort('poBalanceWithPending')}
                  >
                    <div className='flex items-center justify-end gap-1'>
                      Balance
                      {sortField === 'poBalanceWithPending' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                    </div>
                  </th>
                  <th 
                    className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700 cursor-pointer hover:bg-slate-200'
                    onClick={() => handleSort('lastLaborDate')}
                  >
                    <div className='flex items-center justify-center gap-1'>
                      Last Labor
                      {sortField === 'lastLaborDate' && (sortDirection === 'asc' ? <ChevronUp className='w-3 h-3' /> : <ChevronDown className='w-3 h-3' />)}
                    </div>
                  </th>
                  <th className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700'>Status</th>
                  <th className='text-center p-3 text-sm font-bold uppercase tracking-wider text-slate-700'></th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => {
                  const daysSinceLabor = getDaysSinceLastLabor(job.lastLaborDate);
                  const isStale = daysSinceLabor && daysSinceLabor > 30;
                  
                  return (
                  <tr key={job.jobId} className={`border-b border-slate-200 hover:bg-slate-50 ${job.isOverPo ? 'bg-red-50' : ''}`}>
                    <td className='p-3 text-sm font-medium text-slate-700'>{job.jobNumber}</td>
                    <td className='p-3 text-sm text-slate-600 max-w-[250px]' title={job.description}>
                      <div className='whitespace-normal break-words'>
                        {job.description}
                      </div>
                    </td>
                    <td className='p-3 text-right'>
                      <div className='text-sm font-mono font-bold text-slate-800'>
                        {formatCurrency(job.totalChargesWithPending || (job.totalCharges + (job.pendingPoAmount || 0)))}
                      </div>
                      {job.pendingPoAmount > 0 && (
                        <div className='text-xs text-amber-600 italic'>
                          Pending: {formatCurrency(job.pendingPoAmount)}
                        </div>
                      )}
                    </td>
                    {isOfficeOrOwner && (
                      <td className='p-3 text-right'>
                        <div className='text-sm font-mono font-bold text-[#1A3668]'>
                          {formatCurrency(job.invoicedAmount || 0)}
                        </div>
                        {(job.invoicedAmount || 0) > 0 && (
                          <div className='text-xs text-amber-600'>
                            {Math.round(((job.invoicedAmount || 0) / (job.totalChargesWithPending || job.totalCharges || 1)) * 100)}% of charges
                          </div>
                        )}
                      </td>
                    )}
                    <td className={`p-3 text-right text-sm font-mono font-bold ${job.poBalanceWithPending >= 0 ? 'text-[#16A34A]' : 'text-red-600'}`}>
                      {formatCurrency(job.poBalanceWithPending)}
                      {job.isOverPo && job.overageAmount > 0 && (
                        <div className='text-xs text-red-500'>
                          Over by: {formatCurrency(job.overageAmount)}
                        </div>
                      )}
                    </td>
                    <td className='p-3 text-center'>
                      {job.lastLaborDate ? (
                        <div className={`text-sm ${isStale ? 'text-amber-600' : 'text-slate-600'}`}>
                          <div className='font-mono'>{job.lastLaborDate}</div>
                          {isStale && (
                            <div className='text-xs flex items-center justify-center gap-1'>
                              <Clock className='w-3 h-3' />
                              {daysSinceLabor}d ago
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className='text-slate-400 text-sm'>-</span>
                      )}
                    </td>
                    <td className='p-3 text-center'>
                      {job.isOverPo ? (
                        <Badge className='bg-red-100 text-red-800 border-red-200 rounded-sm'>
                          <AlertTriangle className='w-3 h-3 mr-1' />
                          Over Budget
                        </Badge>
                      ) : job.isNearPo ? (
                        <Badge className='bg-amber-100 text-amber-800 border-amber-200 rounded-sm'>Near PO</Badge>
                      ) : (
                        <Badge className='bg-green-100 text-green-800 border-green-200 rounded-sm'>{formatPercent(job.utilizationPct)}</Badge>
                      )}
                    </td>
                    <td className='p-3 text-center'>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => navigate(`/dashboard/job/${job.jobId}`)}
                        className='text-[#1A3668] hover:text-[#142A52]'
                      >
                        <ChevronRight className='w-4 h-4' />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Breakdowns with Tabs */}
      <Tabs defaultValue='labor' className='w-full'>
        <TabsList className='grid w-full grid-cols-2 rounded-sm'>
          <TabsTrigger value='labor' className='rounded-sm'>
            <Users className='w-4 h-4 mr-2' />
            Labor Charges
          </TabsTrigger>
          <TabsTrigger value='material' className='rounded-sm'>
            <Package className='w-4 h-4 mr-2' />
            Material / Sub / Equipment
          </TabsTrigger>
        </TabsList>

        {/* Labor Breakdown Tab */}
        <TabsContent value='labor'>
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardHeader>
              <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
                <CardTitle className='text-xl font-bold text-slate-800'>
                  Labor Charges Breakdown
                  <span className='ml-2 text-lg font-normal text-slate-500'>
                    Total: {formatCurrency(laborBreakdown?.totalLaborCharges)}
                  </span>
                </CardTitle>
                <div className='flex flex-wrap gap-3 items-end'>
                  <div>
                    <Label className='text-xs text-slate-500'>Job</Label>
                    <Select value={selectedJob} onValueChange={setSelectedJob}>
                      <SelectTrigger className='w-[180px] rounded-sm border-2'>
                        <SelectValue placeholder='All Jobs' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All Jobs</SelectItem>
                        {summary?.jobs?.map((job) => (
                          <SelectItem key={job.jobId} value={job.jobId}>{job.jobNumber}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className='text-xs text-slate-500'>From Date</Label>
                    <Input
                      type='date'
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className='w-[150px] rounded-sm border-2'
                    />
                  </div>
                  <div>
                    <Label className='text-xs text-slate-500'>To Date</Label>
                    <Input
                      type='date'
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className='w-[150px] rounded-sm border-2'
                    />
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={exportLaborToExcel}
                    disabled={!laborBreakdown?.entries?.length}
                    className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'
                    data-testid="export-labor-excel"
                  >
                    <Download className='w-4 h-4 mr-2' />
                    Export Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Labor Summary Row */}
              <div className='grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 p-4 bg-slate-50 rounded-sm'>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>ST Hours</p>
                  <p className='text-xl font-bold text-slate-700'>{laborBreakdown?.totalHoursST?.toFixed(1) || 0}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>OT Hours</p>
                  <p className='text-xl font-bold text-amber-600'>{laborBreakdown?.totalHoursOT?.toFixed(1) || 0}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>DT Hours</p>
                  <p className='text-xl font-bold text-red-600'>{laborBreakdown?.totalHoursDT?.toFixed(1) || 0}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Total Hours</p>
                  <p className='text-xl font-bold text-[#1A3668]'>{laborBreakdown?.totalHours?.toFixed(1) || 0}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Total Charges</p>
                  <p className='text-xl font-bold text-[#16A34A]'>{formatCurrency(laborBreakdown?.totalLaborCharges)}</p>
                </div>
              </div>

              {/* Labor Table */}
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-sm'>
                  <thead>
                    <tr className='bg-slate-100 border-b-2 border-slate-200'>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Date</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Job #</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>PO #</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Name</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Skill</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>ST</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>OT</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>DT</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Total Hrs</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Charges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laborBreakdown?.entries?.map((entry) => (
                      <tr key={entry.id} className='border-b border-slate-200 hover:bg-slate-50'>
                        <td className='p-2 font-mono text-slate-600'>{entry.date}</td>
                        <td className='p-2 font-medium text-slate-700'>{entry.jobNumber}</td>
                        <td className='p-2 font-mono text-slate-600'>{entry.poNumber}</td>
                        <td className='p-2 font-medium text-slate-700'>{entry.fullName}</td>
                        <td className='p-2 text-slate-600'>{entry.classificationName}</td>
                        <td className='p-2 text-right font-mono text-slate-700'>{entry.hoursST.toFixed(1)}</td>
                        <td className='p-2 text-right font-mono text-amber-600'>{entry.hoursOT.toFixed(1)}</td>
                        <td className='p-2 text-right font-mono text-red-600'>{entry.hoursDT.toFixed(1)}</td>
                        <td className='p-2 text-right font-mono font-bold text-slate-700'>{entry.totalHours.toFixed(1)}</td>
                        <td className='p-2 text-right font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCharge)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {laborBreakdown?.entries?.length === 0 && (
                  <div className='text-center p-8 text-slate-500'>No labor entries found</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Breakdown Tab */}
        <TabsContent value='material'>
          <Card className='border-slate-200 shadow-sm rounded-sm'>
            <CardHeader>
              <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
                <CardTitle className='text-xl font-bold text-slate-800'>
                  Material / Sub / Equipment Breakdown
                  <span className='ml-2 text-lg font-normal text-slate-500'>
                    Total: {formatCurrency(materialBreakdown?.totalMaterialCharges)}
                  </span>
                </CardTitle>
                <div className='flex flex-wrap gap-3 items-end'>
                  <div>
                    <Label className='text-xs text-slate-500'>Job</Label>
                    <Select value={selectedJob} onValueChange={setSelectedJob}>
                      <SelectTrigger className='w-[180px] rounded-sm border-2'>
                        <SelectValue placeholder='All Jobs' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All Jobs</SelectItem>
                        {summary?.jobs?.map((job) => (
                          <SelectItem key={job.jobId} value={job.jobId}>{job.jobNumber}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className='text-xs text-slate-500'>Type</Label>
                    <Select value={materialType} onValueChange={setMaterialType}>
                      <SelectTrigger className='w-[180px] rounded-sm border-2'>
                        <SelectValue placeholder='All Types' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='all'>All Types</SelectItem>
                        <SelectItem value='MATERIAL'>Material</SelectItem>
                        <SelectItem value='EQUIPMENT'>Equipment</SelectItem>
                        <SelectItem value='SUBCONTRACTOR'>Subcontractor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={exportMaterialToExcel}
                    disabled={!materialBreakdown?.entries?.length}
                    className='rounded-sm border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white'
                    data-testid="export-material-excel"
                  >
                    <Download className='w-4 h-4 mr-2' />
                    Export Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Material Summary Row */}
              <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-slate-50 rounded-sm'>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Subtotal</p>
                  <p className='text-xl font-bold text-slate-700'>{formatCurrency(materialBreakdown?.totalAmount)}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Total Markup</p>
                  <p className='text-xl font-bold text-amber-600'>{formatCurrency(materialBreakdown?.totalMarkup)}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Total Invoices</p>
                  <p className='text-xl font-bold text-[#1A3668]'>{materialBreakdown?.entryCount || 0}</p>
                </div>
                <div>
                  <p className='text-xs font-bold uppercase text-slate-500'>Total Cost</p>
                  <p className='text-xl font-bold text-[#16A34A]'>{formatCurrency(materialBreakdown?.totalMaterialCharges)}</p>
                </div>
              </div>

              {/* By Type Summary */}
              {materialBreakdown?.byType && Object.keys(materialBreakdown.byType).length > 0 && (
                <div className='flex gap-4 mb-4'>
                  {Object.entries(materialBreakdown.byType).map(([type, data]) => (
                    <Badge key={type} variant='outline' className='px-3 py-2 rounded-sm border-2'>
                      <span className='font-bold mr-2'>{type}:</span>
                      <span className='font-mono'>{formatCurrency(data.totalCharge)}</span>
                      <span className='text-slate-400 ml-1'>({data.count})</span>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Material Table */}
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse text-sm'>
                  <thead>
                    <tr className='bg-slate-100 border-b-2 border-slate-200'>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Job #</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Customer PO</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Material PO</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Vendor</th>
                      <th className='text-left p-2 font-bold uppercase tracking-wider text-slate-700'>Type</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Amount</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Markup</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Taxes</th>
                      <th className='text-right p-2 font-bold uppercase tracking-wider text-slate-700'>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialBreakdown?.entries?.map((entry) => (
                      <tr key={entry.id} className='border-b border-slate-200 hover:bg-slate-50'>
                        <td className='p-2 font-medium text-slate-700'>{entry.jobNumber}</td>
                        <td className='p-2 font-mono text-slate-600'>{entry.customerPoNumber}</td>
                        <td className='p-2 font-mono text-slate-600'>{entry.materialPoNumber}</td>
                        <td className='p-2 font-medium text-slate-700'>{entry.vendorName}</td>
                        <td className='p-2'>
                          <Badge className={`rounded-sm text-xs ${
                            entry.type === 'MATERIAL' ? 'bg-blue-100 text-blue-800' :
                            entry.type === 'EQUIPMENT' ? 'bg-orange-100 text-orange-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {entry.type}
                          </Badge>
                        </td>
                        <td className='p-2 text-right font-mono text-slate-700'>{formatCurrency(entry.amount)}</td>
                        <td className='p-2 text-right font-mono text-amber-600'>
                          {formatCurrency(entry.markupAmount)}
                          <span className='text-xs text-slate-400 ml-1'>({entry.markupPct}%)</span>
                        </td>
                        <td className='p-2 text-right font-mono text-slate-500'>{formatCurrency(entry.taxes)}</td>
                        <td className='p-2 text-right font-mono font-bold text-[#16A34A]'>{formatCurrency(entry.totalCost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {materialBreakdown?.entries?.length === 0 && (
                  <div className='text-center p-8 text-slate-500'>No material entries found</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
